import { uploadToBlurby, uploadToBlurbyStatus } from '../utils/zoomServices';
import { takeLatest, put, delay, call } from 'redux-saga/effects';
import { emitUploadFailedEvent } from '../utils/ct-events';
import { recordRumEvent } from '../utils/rum';

const ADD_MESSAGE = 'messages/ADD_MESSAGE';
const UPLOAD_TO_BLURBY = 'message/UPLOAD_TO_BLURBY';
const SET_TIMEOUT_ERROR = 'messages/SET_TIMEOUT_ERROR';

const reducer = function reducer(state = {}, action) {
  const { jobID } = action;
  switch (action?.type) {
    case ADD_MESSAGE:
      const existingMessages = state[jobID] || {
        jobMessages: [],
        timeoutError: false,
      };
      return {
        ...state,
        [jobID]: {
          jobMessages: [...existingMessages.jobMessages, action.message],
          timeoutError: existingMessages.timeoutError,
        },
      };

    case SET_TIMEOUT_ERROR:
      return {
        ...state,
        [jobID]: {
          ...state[jobID],
          timeoutError: true,
        },
      };
    default:
      return state;
  }
};

export function addMessage(jobID, message) {
  return { type: ADD_MESSAGE, jobID, message };
}

function setTimeoutError(jobID) {
  return {
    type: SET_TIMEOUT_ERROR,
    jobID,
  };
}

export function uploadToBlurb(jobPK) {
  return {
    type: UPLOAD_TO_BLURBY,
    jobPK,
  };
}

/**
 * Sagas
 */

export function* sendFileToBlurby(action) {
  try {
    const result = yield uploadToBlurby(action.jobPK).catch((e) => {
      if (e?.response?.status === 504) {
        console.warn('Timeout error while uploading to Blurby API');
        // Our call to the PHP service timed out, but the file will likely still be
        // uploaded, so we'll just start polling for status updates.
        return;
      }
      throw e;
    });

    // result will be undefined if the upload call timed out, but the upload may still succeed on the backend
    if (!result || result.uploaded) {
      yield call(waitForBlurbyProcessingComplete, action.jobPK);
    } else {
      console.error('Failed to upload');
      emitUploadFailedEvent(
        action.jobPK,
        'F',
        'Failed to upload to blurby api',
      );
    }
  } catch (e) {
    console.error(e);
    emitUploadFailedEvent(action.jobPK, 'F', e.message);
  }
}

function* waitForBlurbyProcessingComplete(jobPK) {
  // Blurby processing can take several minutes for large (high page count) books.
  // Keep polling for up to 10 minutes, but surface the "this is taking a while"
  // warning to the user after 2 minutes while we continue polling in the background.
  const WARN_AFTER = 120000; // 2 minutes
  const MAX_DURATION = 600000; // 10 minutes
  let done = false;
  let warned = false;
  let retry = 0;
  let startTime = Date.now();

  while (!done) {
    retry++;
    const elapsedTime = Date.now() - startTime;
    if (!warned && elapsedTime > WARN_AFTER) {
      yield put(setTimeoutError(jobPK));
      warned = true;
    }
    if (elapsedTime > MAX_DURATION) {
      break;
    }

    const statusResult = yield uploadToBlurbyStatus(jobPK);
    const project_id = statusResult.project_id;

    if (statusResult.status === 'success') {
      done = true;
      yield put(
        addMessage(jobPK, {
          message: 'upload completed',
          status: 'uploaded',
          project_id,
        }),
      );
      let timeDuration = Date.now() - startTime;
      recordRumEvent('complete_project_event', {
        timeTakenToUploadFileToBlurby: timeDuration,
      });
    } else {
      yield put(
        addMessage(jobPK, {
          message: statusResult.status,
          retry,
          status: statusResult.status,
        }),
      );
      yield delay(1000);
    }
  }
}

function* setupSagas() {
  yield takeLatest(UPLOAD_TO_BLURBY, sendFileToBlurby);
}

/** ***********************************************************************
 *  Main Export
 *  *********************************************************************** */
export default {
  reducer: reducer,
  saga: setupSagas,
};
